import { Division, Office } from "@gettactic/api";
import { useDivisionsMenuQuery } from "@gettactic/hooks";
import { divisionAndOfficesToCategorySelectItems } from "@cmp-src/divisions/DivisionSelector/helpers";
import { Category } from "@cmp-src/form/CategorySelect/types";
import { Button } from "@cmp-src/primitives/button";
import { Checkbox } from "@cmp-src/primitives/checkbox";
import { Label } from "@cmp-src/primitives/label";
import { Popover } from "@cmp-src/overlay/Popover/Popover";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";
import { TextInput } from "@cmp-src/form/TextInput";
import {
  addParentReferences,
  findCategoryPath,
  flattenCategories
} from "@cmp-src/form/CategorySelect/helpers";
import Fuse from "fuse.js";
import {
  SearchIcon,
  ArrowLeftIcon,
  ArrowRightIcon
} from "@cmp-src/icons/icons";
import { useDisclosure } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import { cn } from "@gettactic/helpers";
import React from "react";
import type { DivisionSelectorProps } from "./DivisionSelector";
import { ModalButtons } from "@cmp-src/overlay/ModalButtons/ModalButtons";
import { Slot } from "@radix-ui/react-slot";
import { XIcon } from "lucide-react";

export const DivisionSelectorWithApply: React.FC<DivisionSelectorProps> = (
  props
) => {
  const {
    hasAllItem,
    selectedDivision,
    selectedOffice,
    onSelectDivision,
    onSelectOffice,
    triggerProps,
    withApply = false,
    isMultiselect = false,
    selectedOffices = [],
    selectedDivisions = [],
    onSelectOffices,
    onSelectDivisions,
    trigger
  } = props;

  const divisions = useDivisionsMenuQuery();
  const root = divisions.data;

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const popoverDisclosure = useDisclosure();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [navigationStack, setNavigationStack] = React.useState<Category[]>([]);

  // For multiselect: track pending selections (before Apply is clicked)
  const [pendingSelectedOfficeIds, setPendingSelectedOfficeIds] =
    React.useState<Set<string>>(new Set());
  const [pendingSelectedDivisionIds, setPendingSelectedDivisionIds] =
    React.useState<Set<string>>(new Set());

  // Track previous open state to detect when popover transitions from closed to open
  const prevIsOpenRef = React.useRef(popoverDisclosure.isOpen);

  // Initialize pending selections from props only when popover opens (not on every prop change)
  React.useEffect(() => {
    const wasClosed = !prevIsOpenRef.current;
    const isNowOpen = popoverDisclosure.isOpen;

    // Only initialize when popover transitions from closed to open
    if (isNowOpen && wasClosed) {
      if (isMultiselect) {
        const officeIds = new Set(selectedOffices.map((o) => o.id));
        const divisionIds = new Set(selectedDivisions.map((d) => d.id));
        setPendingSelectedOfficeIds(officeIds);
        setPendingSelectedDivisionIds(divisionIds);
      } else {
        const officeIds = new Set<string>();
        const divisionIds = new Set<string>();
        if (selectedOffice) officeIds.add(selectedOffice.id);
        if (selectedDivision) divisionIds.add(selectedDivision.id);
        setPendingSelectedOfficeIds(officeIds);
        setPendingSelectedDivisionIds(divisionIds);
      }
    }

    // Update ref for next render
    prevIsOpenRef.current = popoverDisclosure.isOpen;
  }, [
    popoverDisclosure.isOpen,
    isMultiselect,
    selectedOffices,
    selectedDivisions,
    selectedOffice,
    selectedDivision
  ]);

  const categories = React.useMemo(() => {
    const categories: Category = {
      name: "Divisions",
      id: "divisions",
      children: []
    };
    if (root?.tree) {
      categories.children =
        divisionAndOfficesToCategorySelectItems(root.tree)?.children || [];
    }

    // No office option removed - use Clear Selection button instead
    return categories;
  }, [root?.tree]);

  const categoriesWithParents = React.useMemo(() => {
    return addParentReferences(categories);
  }, [categories]);

  const { fuse } = React.useMemo(() => {
    const allCats = flattenCategories(categoriesWithParents);
    const fuseInstance = new Fuse(allCats, {
      keys: ["name", "keywords"],
      threshold: 0.3,
      distance: 100
    });
    return { fuse: fuseInstance, allCategories: allCats };
  }, [categoriesWithParents]);

  const currentCategories = React.useMemo(() => {
    if (searchTerm) {
      const results = fuse.search(searchTerm);
      return results.map((result) => result.item);
    }
    const currentParent =
      navigationStack[navigationStack.length - 1] || categoriesWithParents;
    return currentParent.children || [];
  }, [navigationStack, searchTerm, fuse, categoriesWithParents]);

  const handleSelect = (category: Category) => {
    if (category.children) {
      // Navigate into division
      if (searchTerm) {
        const path = findCategoryPath(category.id, categoriesWithParents);
        setNavigationStack([...path, category]);
        setSearchTerm("");
      } else {
        setNavigationStack([...navigationStack, category]);
      }
    } else {
      // Final item (office or division without children)
      const isOffice = category.id.startsWith("offi_");

      if (isMultiselect) {
        let newOfficeIds: Set<string> | undefined;
        let newDivisionIds: Set<string> | undefined;

        if (isOffice) {
          newOfficeIds = new Set(pendingSelectedOfficeIds);
          if (newOfficeIds.has(category.id)) {
            newOfficeIds.delete(category.id);
          } else {
            newOfficeIds.add(category.id);
          }
          setPendingSelectedOfficeIds(newOfficeIds);
        } else {
          newDivisionIds = new Set(pendingSelectedDivisionIds);
          if (newDivisionIds.has(category.id)) {
            newDivisionIds.delete(category.id);
          } else {
            newDivisionIds.add(category.id);
          }
          setPendingSelectedDivisionIds(newDivisionIds);
        }

        if (!withApply) {
          // Apply immediately if no apply button
          applySelections(
            newOfficeIds ?? pendingSelectedOfficeIds,
            newDivisionIds ?? pendingSelectedDivisionIds
          );
        }
      } else {
        // Single select
        if (withApply) {
          // Store pending selection
          if (isOffice) {
            setPendingSelectedOfficeIds(new Set([category.id]));
            setPendingSelectedDivisionIds(new Set());
          } else {
            setPendingSelectedDivisionIds(new Set([category.id]));
            setPendingSelectedOfficeIds(new Set());
          }
        } else {
          // Apply immediately
          if (isOffice) {
            onSelectOffice?.(category.data as Office);
            onSelectDivision?.(undefined);
          } else {
            onSelectDivision?.(category.data as Division);
            onSelectOffice?.(undefined);
          }
          popoverDisclosure.close();
        }
      }
    }
  };

  const applySelections = (
    officeIds?: Set<string>,
    divisionIds?: Set<string>
  ) => {
    const finalOfficeIds = officeIds ?? pendingSelectedOfficeIds;
    const finalDivisionIds = divisionIds ?? pendingSelectedDivisionIds;

    if (isMultiselect) {
      const allOffices = flattenCategories(categoriesWithParents)
        .filter((c) => c.id.startsWith("offi_"))
        .map((c) => c.data as Office)
        .filter(Boolean);
      const allDivisions = flattenCategories(categoriesWithParents)
        .filter((c) => !c.id.startsWith("offi_") && c.id !== "no_office")
        .map((c) => c.data as Division)
        .filter(Boolean);

      const selectedOffices = allOffices.filter((o) =>
        finalOfficeIds.has(o.id)
      );
      const selectedDivisions = allDivisions.filter((d) =>
        finalDivisionIds.has(d.id)
      );

      onSelectOffices?.(selectedOffices);
      onSelectDivisions?.(selectedDivisions);
    } else {
      const allOffices = flattenCategories(categoriesWithParents)
        .filter((c) => c.id.startsWith("offi_"))
        .map((c) => c.data as Office)
        .filter(Boolean);
      const allDivisions = flattenCategories(categoriesWithParents)
        .filter((c) => !c.id.startsWith("offi_") && c.id !== "no_office")
        .map((c) => c.data as Division)
        .filter(Boolean);

      const selectedOffice =
        finalOfficeIds.size > 0
          ? allOffices.find((o) => finalOfficeIds.has(o.id))
          : undefined;
      const selectedDivision =
        finalDivisionIds.size > 0
          ? allDivisions.find((d) => finalDivisionIds.has(d.id))
          : undefined;

      onSelectOffice?.(selectedOffice);
      onSelectDivision?.(selectedDivision);
    }

    popoverDisclosure.close();
  };

  const handleApply = () => {
    applySelections();
  };

  const handleCancel = () => {
    // Clear all selections
    setPendingSelectedOfficeIds(new Set());
    setPendingSelectedDivisionIds(new Set());

    // Apply the cleared selection immediately
    if (isMultiselect) {
      onSelectOffices?.([]);
      onSelectDivisions?.([]);
    } else {
      onSelectOffice?.(undefined);
      onSelectDivision?.(undefined);
    }

    popoverDisclosure.close();
  };

  const handleBack = () => {
    setNavigationStack(navigationStack.slice(0, -1));
  };

  const handleOpenChange = (open: boolean) => {
    popoverDisclosure.onOpenChange(open);
    if (!open) {
      setTimeout(() => {
        setNavigationStack([]);
        setSearchTerm("");
      }, 200);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const isCategorySelected = (category: Category): boolean => {
    const isOffice = category.id.startsWith("offi_");
    if (isOffice) {
      return pendingSelectedOfficeIds.has(category.id);
    }
    return pendingSelectedDivisionIds.has(category.id);
  };

  const getTriggerLabel = () => {
    if (isMultiselect) {
      const totalSelected =
        pendingSelectedOfficeIds.size + pendingSelectedDivisionIds.size;
      if (totalSelected === 0) {
        return "Select offices/divisions";
      }
      return `${totalSelected} selected`;
    }

    if (hasAllItem && !selectedOffice && !selectedDivision) {
      return "All Offices and Divisions";
    }
    if (selectedOffice) {
      return selectedOffice.name;
    }
    if (selectedDivision) {
      return selectedDivision.name;
    }
    return "Select office/division";
  };

  const triggerContent = trigger || (
    <Button
      variant={"trigger"}
      {...triggerProps}
      className={cn("w-full min-w-0", triggerProps?.className)}
    >
      <span className={"min-w-0 flex-1 truncate text-left"}>
        {getTriggerLabel()}
      </span>
    </Button>
  );

  const renderCategoryItem = (category: Category, isBack = false) => {
    const path = findCategoryPath(category.id, categoriesWithParents);
    const isSelected = isCategorySelected(category);
    const isFinalItem = !category.children;
    const showCheckbox = isMultiselect && isFinalItem;

    // For final items with checkboxes, use GenericSelect-style rendering
    if (showCheckbox) {
      // Extract text from category.data to avoid rendering icons
      const displayName =
        (category.data as Office | Division)?.name || category.name;

      // A real checkbox with its label, so the row works from the keyboard too
      const checkboxId = `division-selector-${category.id}`;
      return (
        <div
          key={category.id}
          className="group relative flex cursor-pointer items-start px-v2-md hover:bg-muted active:bg-muted"
        >
          <div className="pt-v2-md">
            <Checkbox
              id={checkboxId}
              checked={isSelected}
              onCheckedChange={() => handleSelect(category)}
            />
          </div>
          <Label
            htmlFor={checkboxId}
            className={cn("flex-1 cursor-pointer py-2 pl-2 text-sm")}
          >
            {displayName}
          </Label>
        </div>
      );
    }

    // For navigation items (divisions with children) or back button, use Button
    return (
      <Button
        key={category.id}
        variant="ghost"
        className="w-full justify-start bg-white px-v2-md hover:bg-neutral-100"
        onClick={() => (isBack ? handleBack() : handleSelect(category))}
      >
        {!isBack && navigationStack.length > 0 && <div className="size-4" />}
        {isBack && <ArrowLeftIcon className="size-4" />}
        <div className="flex flex-1 flex-col text-left">
          <div>{category.name}</div>
          {searchTerm && path.length > 0 && (
            <div className="mt-0.5 flex gap-1 overflow-hidden text-ellipsis text-muted-foreground text-xs">
              {path.map((c, index) => (
                <React.Fragment key={index}>
                  <div className="inline-block">{c.name}</div>
                  {index < path.length - 1 && " → "}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        {!isBack && category.children && !searchTerm && (
          <ArrowRightIcon className="size-4" />
        )}
      </Button>
    );
  };

  const content = (
    <>
      <div className="w-full">
        <div className="relative border-b-1 border-b-neutral-100 p-0.5">
          <TextInput
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
            wrapperClassName="border-0 shadow-none px-4"
            inputClassName="h-5 py-3 outline-none"
            leftIcon={<SearchIcon className="size-4" />}
          />
          {searchTerm && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={handleClearSearch}
              className="absolute top-2.5 right-2 text-content hover:text-foreground"
            >
              <XIcon aria-hidden className="size-4" />
            </button>
          )}
        </div>
        <ScrollArea className="h-[250px] w-full">
          <div className="w-full">
            {navigationStack.length > 0 && !searchTerm && (
              <div>
                {renderCategoryItem(
                  navigationStack[navigationStack.length - 1],
                  true
                )}
              </div>
            )}
            <div className="w-full">
              {currentCategories.map((category) =>
                renderCategoryItem(category)
              )}
            </div>
          </div>
        </ScrollArea>
        {withApply && (
          <div className="flex h-[44px] select-none items-center justify-between border-t border-neutral-100 px-1">
            <ModalButtons onApply={handleApply} onCancel={handleCancel} />
          </div>
        )}
      </div>
    </>
  );

  // The default trigger is already a button; a custom `trigger` is markup that needs one.
  // Wrapping a button in another would nest them.
  const isCustomTrigger = !!trigger;

  if (!isMd) {
    return (
      <>
        {isCustomTrigger ? (
          <button
            type="button"
            aria-expanded={popoverDisclosure.isOpen}
            onClick={() => popoverDisclosure.toggle()}
            className="group inline-flex max-w-full min-w-0 gap-2 text-left"
          >
            {triggerContent}
          </button>
        ) : (
          <Slot
            aria-expanded={popoverDisclosure.isOpen}
            onClick={() => popoverDisclosure.toggle()}
          >
            {triggerContent}
          </Slot>
        )}
        <MobileDrawer
          ariaLabel="Offices and divisions"
          isOpen={popoverDisclosure.isOpen}
          onOpenChange={handleOpenChange}
        >
          {content}
        </MobileDrawer>
      </>
    );
  }

  return (
    <Popover
      className="w-[252px]"
      bodyProps={{ className: "p-0 w-full" }}
      content={content}
      open={popoverDisclosure.isOpen}
      onOpenChange={handleOpenChange}
      triggerAsChild={!isCustomTrigger}
      contentProps={{ "aria-label": "Offices and divisions" }}
    >
      {triggerContent}
    </Popover>
  );
};
